import { apiFetch } from "./client";
import { auth } from "../firebase";

// ---- Admin ----

export function listQuotes({ status } = {}) {
  const params = new URLSearchParams();
  if (status) params.set("status", status);
  const qs = params.toString();
  return apiFetch(`/api/admin/quotes${qs ? `?${qs}` : ""}`);
}

export function getQuote(quoteId) {
  return apiFetch(`/api/admin/quotes/${quoteId}`);
}

export function createQuote(payload) {
  return apiFetch("/api/admin/quotes", { method: "POST", body: payload });
}

export function updateQuote(quoteId, payload) {
  return apiFetch(`/api/admin/quotes/${quoteId}`, { method: "PUT", body: payload });
}

export function deleteQuote(quoteId) {
  return apiFetch(`/api/admin/quotes/${quoteId}`, { method: "DELETE" });
}

export function sendQuoteToCustomer(quoteId) {
  return apiFetch(`/api/admin/quotes/${quoteId}/send`, { method: "POST", body: {} });
}

// A quote an API key asked to send. Approving sends exactly the version you
// reviewed (updated_at); if it changed since, the server refuses.
export function approveQuoteSend(quoteId, updatedAt) {
  return apiFetch(`/api/admin/quotes/${quoteId}/approve-send`, { method: "POST", body: { updated_at: updatedAt } });
}

// Don't send; hand the draft back with a reason the API client can read.
export function sendBackQuote(quoteId, reason) {
  return apiFetch(`/api/admin/quotes/${quoteId}/send-back`, { method: "POST", body: { reason } });
}

// Write off a quote that isn't going to book: removes its calendar hold and
// deactivates its payment links. Refused once money has come in.
export function markQuoteLost(quoteId, reason) {
  return apiFetch(`/api/admin/quotes/${quoteId}/lost`, { method: "POST", body: { reason } });
}

export function reopenQuote(quoteId) {
  return apiFetch(`/api/admin/quotes/${quoteId}/reopen`, { method: "POST", body: {} });
}

// Create (or refresh) a Square payment link for a quote. Amount defaults to the
// deposit (or full total) server-side; pass amountCents to override.
export function createQuotePaymentLink(quoteId, amountCents) {
  const body = {};
  if (amountCents != null) body.amount_cents = amountCents;
  return apiFetch(`/api/admin/quotes/${quoteId}/payment-link`, {
    method: "POST",
    body,
  });
}

// ---- Public (no login): the customer view & pay page ----

export function getPublicQuote(token) {
  return apiFetch(`/api/public/quotes/${token}`);
}

/**
 * Tell the server the customer opened their quote (you get a "they're
 * looking" alert). Best-effort and fire-and-forget: never throws, never
 * affects the page. Waits briefly for a signed-in session so your own looks
 * at the page are recognised and not counted.
 */
export async function markQuoteSeen(token) {
  try {
    await Promise.race([auth.authStateReady(), new Promise((r) => setTimeout(r, 3000))]);
    await apiFetch(`/api/public/quotes/${token}/seen`, { method: "POST", body: {} });
  } catch {
    /* not worth bothering the customer about */
  }
}

export function publicQuotePdfUrl(token) {
  const base = (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000").replace(/\/$/, "");
  return `${base}/api/public/quotes/${token}/pdf`;
}

// ---- PDF ----

/**
 * Fetch a quote PDF with auth headers and open it in a new tab.
 * The <a href> approach can't send an Authorization header, so we pull the
 * bytes and hand the browser a blob URL instead.
 */
export async function openQuotePdf(quoteId) {
  const base = (process.env.REACT_APP_API_BASE_URL || "http://localhost:5000").replace(/\/$/, "");
  const path = `/api/admin/quotes/${quoteId}/pdf`;

  const token = await auth.currentUser?.getIdToken();
  const res = await fetch(`${base}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `Could not load PDF (${res.status})`);
  }

  const blobUrl = URL.createObjectURL(await res.blob());
  window.open(blobUrl, "_blank", "noopener");
  // Give the new tab time to load before releasing the object URL.
  setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
}

// Rain-outs: preview what the customer keeps as credit, record it, or undo it.
export function previewRainOut(quoteId, body) {
  return apiFetch(`/api/admin/quotes/${quoteId}/rain-out/preview`, { method: "POST", body });
}

export function recordRainOut(quoteId, body) {
  return apiFetch(`/api/admin/quotes/${quoteId}/rain-out`, { method: "POST", body });
}

export function undoRainOut(quoteId) {
  return apiFetch(`/api/admin/quotes/${quoteId}/rain-out`, { method: "DELETE" });
}

/** A customer's credits with money left (counting what `quoteId` already uses). */
export function listCustomerCredits(customerId, quoteId) {
  const q = quoteId ? `?quote_id=${quoteId}` : "";
  return apiFetch(`/api/admin/customers/${customerId}/credits${q}`);
}
